import { ENDPOINT, ENDPOINT_LOCAL } from "./config";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AdminViewSummaryDonationItem,
  ChildItem,
  DonationItem,
  SummaryAdmin,
  SummaryTree,
  WishType,
} from "@shared/types";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "./auth";

export const FUNCTIONS_BASE_URL = (
  process.env.NODE_ENV === "development" ? ENDPOINT_LOCAL : ENDPOINT
).replace(/\/+$/, "");

export const getWishItemKey = (wishType: WishType): "toyWishes" | "apparelWishes" =>
  wishType === "ALL" || wishType === "TOY" ? "toyWishes" : "apparelWishes";

type RequestError = { status: number };

function getTreeData(): Promise<SummaryTree> {
  return fetchWrapper(`${FUNCTIONS_BASE_URL}/tree`);
}

export function useGetTreeData(queryOptions = {}) {
  return useQuery<SummaryTree, RequestError, { responseData: SummaryTree; allDonated: boolean }>({
    queryKey: ["tree", "list"],
    queryFn: () => getTreeData(),
    select: data => {
      const flattenOrnaments = [];
      if (data.ids.length) {
        data.ids.forEach(id => flattenOrnaments.push(...data.details[id]));
      }
      const allDonated =
        !!flattenOrnaments.length && flattenOrnaments.filter(child => !child.donated).length === 0;
      return { responseData: data, allDonated };
    },
    ...queryOptions,
  });
}

async function getAdminSummary() {
  return fetchWrapper(`${FUNCTIONS_BASE_URL}/admin`, {}, true);
}

export function useGetAdminSummary(queryOptions = {}) {
  return useQuery<SummaryAdmin, RequestError>({
    queryKey: ["admin", "list"],
    queryFn: () => getAdminSummary(),
    ...queryOptions,
  });
}

export type SchoolAdminItem = {
  username: string;
  email: string;
  status: string;
  enabled: boolean;
  createdDate: string;
};

export function useGetAdmins(queryOptions = {}) {
  return useQuery<SchoolAdminItem[], RequestError>({
    queryKey: ["admins", "list"],
    queryFn: () => fetchWrapper(`${FUNCTIONS_BASE_URL}/admins`, {}, true),
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
}

async function getChildInfo(id: string, itemType: WishType) {
  return fetchWrapper(`${FUNCTIONS_BASE_URL}/child/info/${id}/${itemType}`);
}

export type ChildInfoResponse = Partial<Pick<ChildItem, "toyWishes" | "apparelWishes">> &
  Pick<ChildItem, "age" | "gender">;

export function useGetChildInfo(id: string, itemType: WishType, queryOptions = {}) {
  return useQuery<ChildInfoResponse>({
    queryKey: ["tree", "details", id, itemType],
    queryFn: () => getChildInfo(id, itemType),
    ...queryOptions,
  });
}

export async function getChildDetails(id: string) {
  return fetchWrapper(`${FUNCTIONS_BASE_URL}/child/details/${id}`, {}, true);
}

export function useChildDetails(id: string) {
  return useQuery<ChildItem, RequestError>({
    queryKey: ["children", "detail", id],
    queryFn: () => getChildDetails(id),
  });
}

export async function getDonationDetails(id: string) {
  return fetchWrapper(`${FUNCTIONS_BASE_URL}/donation/details/${id}`, {}, true);
}

export function useDonationDetails(id: string) {
  return useQuery<DonationItem, RequestError>({
    queryKey: ["donations", "detail", id],
    queryFn: () => getDonationDetails(id),
  });
}

export async function postDonation(data) {
  const { childId } = data;
  return await fetchWrapper(`${FUNCTIONS_BASE_URL}/donate/${childId}`, {
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(data),
  });
}

export async function postNewChild(data) {
  const token = await getAuthToken();
  return await fetch(`${FUNCTIONS_BASE_URL}/child`, {
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(data),
  });
}

export async function updateChild(data) {
  const token = await getAuthToken();
  return await fetch(`${FUNCTIONS_BASE_URL}/child/${data.id}`, {
    method: "PUT",
    mode: "cors",
    headers: {
      "content-type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(data),
  });
}

export async function deleteChild(id: string) {
  const token = await getAuthToken();
  return await fetch(`${FUNCTIONS_BASE_URL}/child/${id}`, {
    method: "DELETE",
    mode: "cors",
    headers: {
      "content-type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
  });
}

async function updateDonation(data) {
  return await fetchWrapper(
    `${FUNCTIONS_BASE_URL}/donation/${data.id}`,
    {
      method: "PUT",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(data),
    },
    true
  );
}

export function useUpdateDonation() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation<
    Partial<AdminViewSummaryDonationItem>,
    RequestError,
    {
      donation: Partial<Omit<AdminViewSummaryDonationItem, "received">> & { received?: boolean };
    }
  >({
    mutationFn: ({ donation }) => updateDonation(donation),
    onSuccess: (data, { donation }) => {
      // update cache before new data from ["admin", "list"]
      queryClient.setQueryData<SummaryAdmin>(["admin", "list"], prevData => {
        const updatedData: SummaryAdmin = JSON.parse(JSON.stringify(prevData));
        updatedData.donations.detail[donation.id] = {
          ...updatedData.donations.detail[donation.id],
          ...data,
        };
        return updatedData;
      });
      queryClient.refetchQueries({ queryKey: ["admin", "list"] });
      queryClient.removeQueries({ queryKey: ["donations", "detail", donation.id] });
    },
    onError: () => navigate("/login"),
  });
}

async function deleteDonation(donationId: string, childId: string, itemType: WishType) {
  const token = await getAuthToken();
  return await fetch(`${FUNCTIONS_BASE_URL}/donation/${donationId}`, {
    method: "DELETE",
    mode: "cors",
    headers: {
      "content-type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify({ childId, itemType }),
  });
}

export function useDeleteDonation() {
  return useMutation<
    unknown,
    RequestError,
    { donationId: string; childId: string; itemType: WishType }
  >({
    mutationFn: ({ donationId, childId, itemType }) =>
      deleteDonation(donationId, childId, itemType),
  });
}

async function fetchWrapper(url: string, config?: RequestInit, requiresAuth = false) {
  const headers = new Headers(config?.headers);
  if (requiresAuth) {
    const token = await getAuthToken();
    if (token) {
      headers.set("Authorization", token);
    }
  }
  const response = await fetch(url, { ...config, headers });
  if (response.ok) {
    return await response.json();
  }
  return Promise.reject(response);
}

export function doNotRetryOn4xx(failureCount, error) {
  if (error.status >= 400 && error.status < 500) {
    return false;
  }
  if (failureCount >= 3) {
    return false;
  }
}
